import { type PolymorphicProps } from '@mui/base'
import { clsx } from 'clsx'
import { type ComponentProps, type ElementType, type JSX } from 'react'

type SkeletonOwnProps = {
  slotProps?: {
    root?: ComponentProps<'span'>
  }

  slots?: {
    root?: ElementType
  }

  /**
   * The type of content that will be rendered
   * @default text
   */
  variant?: 'circular' | 'rectangular' | 'text'
}

interface SkeletonTypeMap<RootComponentType extends ElementType = 'span'> {
  defaultComponent: RootComponentType
  props: SkeletonOwnProps
}

type SkeletonProps<
  RootComponentType extends ElementType = SkeletonTypeMap['defaultComponent'],
> = PolymorphicProps<SkeletonTypeMap<RootComponentType>, RootComponentType>

/**
 * Display a placeholder preview of your content before the data gets loaded
 * to reduce load-time frustration
 */
export const Skeleton = (props: SkeletonProps): JSX.Element => {
  const { className, slots = {}, variant = 'text', ...rest } = props

  const Root = slots.root ?? 'span'

  return (
    <Root
      {...rest}
      className={clsx(
        'skeleton',
        variant === 'circular' && 'skeleton_circular',
        variant === 'rectangular' && 'skeleton_rectangular',
        variant === 'text' && 'skeleton_text',
        className,
      )}
      role="none"
    />
  )
}
